/**
 * LoginPage.jsx
 * CCH Care — Auth
 * Last Modified: 2026-04-21
 *
 * Changes (2026-04-21) — Feature #11 (forgot/reset password):
 * - Added resetSuccess banner shown when URL has ?reset=success.
 *   Mirrors the existing ?expired=1 pattern: read once, strip from URL
 *   on mount so refresh doesn't re-show. Uses authSuccessBg/Border
 *   tokens added to constants.js in the 2026-04-20 auth palette pass.
 * - "Forgot password?" link was already wired to /forgot-password in
 *   the 2026-04-17 remember-me pass. No change needed there.
 *
 * Changes (2026-04-20) — Auth palette tokenization:
 * - All hardcoded hex and rgba values swapped for COLORS.auth* tokens
 *   defined in constants.js / index.css. No visual change intended —
 *   token values mirror the original hardcoded values exactly.
 * - The shared labelStyle / inputStyle / btnPrimary style constants now
 *   reference tokens. They remain module-level constants (not component-
 *   scoped) because token values are stable JS references to CSS vars,
 *   not dynamic values.
 * - Banner backgrounds and borders (error, session-expired) tokenized as
 *   authErrorBg/Border and authWarningBg/Border so ForgotPassword and
 *   ResetPassword can reuse the same visual treatment without drift.
 * - One-off values kept inline with comments: the nav bottom border,
 *   the logo box shadow, the 2FA setup info-panel bg/border, the QR
 *   code white background, and the 2FA secret display bg. These have no
 *   cross-page reuse and tokenizing would expand the palette for no gain.
 *
 * Changes (2026-04-17):
 * - Remember Me is now functional:
 *   - Checkbox is a controlled input wired to rememberMe state
 *   - Email is prefilled on next visit when Remember Me was last checked
 *   - rememberMe flag passed to login() so useAuth can optionally use it
 *     for longer session persistence (requires useAuth.js update — TODO)
 *
 * Changes (2026-04-02):
 * - Added local error state as fallback when useAuth error doesn't propagate
 * - Error banner made more prominent with icon and stronger contrast
 * - Catches all error shapes from API responses
 * - Subtle hint below error for wrong-app scenarios
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from './useAuth';
import { COLORS } from '../../config/constants';

// localStorage keys for Remember Me
const REMEMBER_FLAG_KEY  = 'cch_remember_me';
const REMEMBER_EMAIL_KEY = 'cch_remember_email';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, verify2FA, error: authError, clearError } = useAuth();

  const [step,         setStep]         = useState('credentials');
  // Initialize email from localStorage if Remember Me was previously set
  const [email,        setEmail]        = useState(() => {
    try {
      const remembered = localStorage.getItem(REMEMBER_FLAG_KEY) === '1';
      return remembered ? (localStorage.getItem(REMEMBER_EMAIL_KEY) || '') : '';
    } catch {
      return '';
    }
  });
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe,   setRememberMe]   = useState(() => {
    try {
      return localStorage.getItem(REMEMBER_FLAG_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [code,         setCode]         = useState('');
  const [tempToken,    setTempToken]    = useState('');
  const [qrCodeUrl,    setQrCodeUrl]    = useState('');
  const [totpSecret,   setTotpSecret]   = useState('');
  const [submitting,   setSubmitting]   = useState(false);

  // Local error state — catches failures even if useAuth doesn't propagate
  const [localError, setLocalError] = useState('');

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const codeRef  = useRef(null);

  const sessionExpired = searchParams.get('expired') === '1';
  // Capture ?reset=success from the initial URL. Kept in state so we can
  // strip the param from the URL (same pattern as ?expired=1) without
  // losing the banner on the current render.
  const [resetSuccess, setResetSuccess] = useState(() => searchParams.get('reset') === 'success');

  // Combine auth error and local error — local takes precedence if set
  const displayError = localError || authError || '';

  function clearErrors() {
    setLocalError('');
    clearError();
  }

  useEffect(() => {
    if (step === 'credentials') {
      // If email was prefilled, focus password instead for faster UX
      if (email && passwordRef.current) {
        passwordRef.current.focus();
      } else if (emailRef.current) {
        emailRef.current.focus();
      }
    } else if ((step === '2fa_setup' || step === '2fa_verify') && codeRef.current) {
      codeRef.current.focus();
    }
    // email intentionally excluded — we only care about the value on step transitions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Clear the ?expired=1 param from the URL after showing the banner
  // so a page refresh doesn't keep displaying it
  useEffect(() => {
    if (sessionExpired) {
      const url = new URL(window.location.href);
      url.searchParams.delete('expired');
      window.history.replaceState({}, '', url.toString());
    }
  }, [sessionExpired]);

  // Same pattern for ?reset=success — strip from URL after capture so a
  // refresh doesn't show the success banner repeatedly. resetSuccess state
  // stays true for the current render so the user sees it once.
  useEffect(() => {
    if (resetSuccess) {
      const url = new URL(window.location.href);
      url.searchParams.delete('reset');
      window.history.replaceState({}, '', url.toString());
    }
  }, [resetSuccess]);

  // Auto-dismiss the reset success banner once the user starts engaging
  // with the form (typing a password, or changing the prefilled email).
  // The banner is low-urgency confirmation, not an error — once they're
  // acting on the message, it's done its job and sits in the way.
  useEffect(() => {
    if (!resetSuccess) return;
    const prefill = (() => {
      try {
        const remembered = localStorage.getItem(REMEMBER_FLAG_KEY) === '1';
        return remembered ? (localStorage.getItem(REMEMBER_EMAIL_KEY) || '') : '';
      } catch { return ''; }
    })();
    if (password.length > 0 || email !== prefill) {
      setResetSuccess(false);
    }
  }, [email, password, resetSuccess]);

  // Persist or clear Remember Me data based on the checkbox state
  function persistRememberMe() {
    try {
      if (rememberMe) {
        localStorage.setItem(REMEMBER_FLAG_KEY, '1');
        localStorage.setItem(REMEMBER_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(REMEMBER_FLAG_KEY);
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }
    } catch {
      // localStorage can throw in private browsing / quota exceeded — ignore
    }
  }

  async function handleCredentials(e) {
    e.preventDefault();
    clearErrors();
    persistRememberMe();
    setSubmitting(true);
    try {
      // Pass rememberMe to login — useAuth can optionally use this to
      // extend session lifetime or switch storage strategy
      const data = await login(email, password, { rememberMe });
      if (data?.requires_2fa_setup) {
          setTempToken(data.temp_token);
          setQrCodeUrl(data.qr_code_url);
          setTotpSecret(data.secret);
          setStep('2fa_setup');
      } else if (data?.requires_2fa) {
          setTempToken(data.temp_token);
          setStep('2fa_verify');
      } else {
          console.error('Login response missing 2FA flag:', data);
          setLocalError('Login completed unexpectedly. Please try again or contact support.');
      }
    } catch (err) {
      // Extract error message from all possible shapes
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        authError ||
        'Login failed. Please check your credentials and try again.';
      setLocalError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify2FA(e) {
    e.preventDefault();
    clearErrors();
    setSubmitting(true);
    try {
      await verify2FA(tempToken, code, { rememberMe });
      navigate('/dashboard');
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        authError ||
        'Verification failed. Please try again.';
      setLocalError(msg);
      setCode('');
      codeRef.current?.focus();   // ← refocus so they can retype immediately
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: COLORS.authBg,
      color: COLORS.authText,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* Top Nav */}
      <nav style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 32px', maxWidth: 1200, width: '100%',
        margin: '0 auto', boxSizing: 'border-box',
        // One-off: nav bottom hairline, no cross-page reuse
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: COLORS.authText }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: COLORS.authBrandBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>🏥</div>
          <span style={{ fontSize: 18, fontWeight: 800 }}>CCH Care</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: COLORS.authAccent, fontSize: 14, fontWeight: 600 }}>Login</span>
          <Link to="/get-started" style={{
            padding: '10px 24px', background: COLORS.authPrimary, border: 'none',
            borderRadius: 8, color: COLORS.authText, fontSize: 14, fontWeight: 700,
            textDecoration: 'none',
          }}>Start Free Trial</Link>
        </div>
      </nav>

      {/* Login Card */}
      <div style={{
        flex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, paddingBottom: '18vh',
      }}>
        <div style={{ width: '100%', maxWidth: 460 }}>

          {/* Icon + Welcome */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16, background: COLORS.authBrandBg,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              // One-off: logo box shadow, no cross-page reuse
              fontSize: 32, marginBottom: 16, boxShadow: '0 4px 16px rgba(27, 107, 147, 0.3)',
            }}>🏥</div>
            <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>
              {step === 'credentials' ? 'Welcome back' : 'Two-Factor Authentication'}
            </h1>
            <p style={{ color: COLORS.authTextHelper, fontSize: 15 }}>
              {step === 'credentials'  ? 'Sign in to your account'
                : step === '2fa_setup'  ? 'Set up your authenticator app'
                : 'Enter your verification code'}
            </p>
          </div>

          {/* Password reset success banner (after completing reset flow) */}
          {resetSuccess && step === 'credentials' && (
            <div style={{
              padding: '12px 16px', borderRadius: 10, marginBottom: 16,
              background: COLORS.authSuccessBg, border: `1px solid ${COLORS.authSuccessBorder}`,
              color: COLORS.authAccent, fontSize: 14, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>✓</span>
              <span>Password reset successfully. Sign in with your new password.</span>
            </div>
          )}

          {/* Session expired banner */}
          {sessionExpired && step === 'credentials' && (
            <div style={{
              padding: '12px 16px', borderRadius: 10, marginBottom: 16,
              background: COLORS.authWarningBg, border: `1px solid ${COLORS.authWarningBorder}`,
              color: COLORS.authWarningText, fontSize: 14, fontWeight: 500, textAlign: 'center',
            }}>
              ⏱️ Your session has expired. Please sign in again.
            </div>
          )}

          {/* ── Error banner ── */}
          {displayError && (
            <div style={{
              padding: '14px 16px', borderRadius: 10, marginBottom: 16,
              background: COLORS.authErrorBg,
              border: `1px solid ${COLORS.authErrorBorder}`,
              color: COLORS.authErrorText,
              fontSize: 14, lineHeight: 1.5,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>⚠️</span>
                <span style={{ fontWeight: 600 }}>{displayError}</span>
              </div>
              {/* Hint for wrong-app logins — always shown after failure */}
              <div style={{ fontSize: 12, color: COLORS.authTextMuted, paddingLeft: 24 }}>
                If you&apos;re a platform admin, sign in at{' '}
                <a
                  href="https://admin.clientcarehealth.org"
                  style={{ color: COLORS.authLink, textDecoration: 'none' }}
                  target="_blank"
                  rel="noreferrer"
                >
                  admin.clientcarehealth.org
                </a>
              </div>
            </div>
          )}

          {/* Card */}
          <div style={{
            background: COLORS.authSurface,
            border: `1px solid ${COLORS.authSurfaceBorder}`,
            borderRadius: 16, padding: 32,
          }}>

            {/* Step 1: Credentials */}
            {step === 'credentials' && (
              <form onSubmit={handleCredentials}>
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="login-email" style={labelStyle}>Email Address</label>
                  <input
                    id="login-email"
                    ref={emailRef}
                    type="email" value={email}
                    onChange={e => { setEmail(e.target.value); clearErrors(); }}
                    placeholder="you@facility.com"
                    required autoComplete="email"
                    style={{
                      ...inputStyle,
                      borderColor: displayError ? COLORS.authErrorBorder : COLORS.authInputBorder,
                    }}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label htmlFor="login-password" style={labelStyle}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="login-password"
                      ref={passwordRef}
                      type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => { setPassword(e.target.value); clearErrors(); }}
                      placeholder="Enter your password"
                      required autoComplete="current-password"
                      style={{
                        ...inputStyle,
                        borderColor: displayError ? COLORS.authErrorBorder : COLORS.authInputBorder,
                      }}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', color: COLORS.authTextHelper,
                        cursor: 'pointer', fontSize: 18, padding: 4,
                      }}
                      tabIndex={-1}
                    >{showPassword ? '🙈' : '👁️'}</button>
                  </div>
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 24, fontSize: 14,
                }}>
                  <label htmlFor="login-remember-me" style={{ display: 'flex', alignItems: 'center', gap: 8, color: COLORS.authTextMuted, cursor: 'pointer' }}>
                    <input
                      id="login-remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                      style={{ accentColor: COLORS.authPrimary, cursor: 'pointer' }}
                    />
                    Remember me
                  </label>
                  <Link to="/forgot-password" style={{ color: COLORS.authLink, fontWeight: 600, textDecoration: 'none' }}>
                    Forgot password?
                  </Link>
                </div>
                <button type="submit" disabled={submitting} style={{
                  ...btnPrimary,
                  opacity: submitting ? 0.7 : 1,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}>
                  {submitting ? 'Signing In…' : 'Sign In'}
                </button>
                <div style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: COLORS.authTextHelper }}>
                  Don&apos;t have an account?{' '}
                  <Link to="/get-started" style={{ color: COLORS.authLink, fontWeight: 600, textDecoration: 'none' }}>
                    Start Free Trial
                  </Link>
                </div>
              </form>
            )}

            {/* Step 2a: 2FA Setup */}
            {step === '2fa_setup' && (
              <form onSubmit={handleVerify2FA}>
                <div style={{
                  textAlign: 'center', marginBottom: 24, padding: 20,
                  // One-off: 2FA setup info panel bg + border, no cross-page reuse
                  background: 'rgba(27, 107, 147, 0.1)', borderRadius: 12,
                  border: '1px solid rgba(27, 107, 147, 0.2)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.authAccent, marginBottom: 12 }}>
                    Scan this QR code with your authenticator app:
                  </div>
                  {qrCodeUrl && (
                    <img src={qrCodeUrl} alt="2FA QR Code"
                      // One-off: QR code bg is literal white for scanner contrast, not a theme concern
                      style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
                  )}
                  <div style={{ marginTop: 12, fontSize: 12, color: COLORS.authTextHelper }}>Or enter manually:</div>
                  <div style={{
                    // One-off: 2FA secret monospace display bg, no cross-page reuse
                    marginTop: 6, padding: '8px 16px', background: 'rgba(255,255,255,0.06)',
                    borderRadius: 8, fontFamily: 'monospace', fontSize: 15,
                    fontWeight: 700, letterSpacing: '0.1em',
                  }}>{totpSecret}</div>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label htmlFor="login-2fa-setup-code" style={labelStyle}>
                    Enter the 6-digit code from your app
                  </label>
                  <input
                    id="login-2fa-setup-code"
                    ref={codeRef}
                    type="text" value={code} maxLength={6} inputMode="numeric"
                    onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); clearErrors(); }}
                    placeholder="000000" required
                    style={{ ...inputStyle, textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: '0.3em' }}
                  />
                </div>
                <button type="submit" disabled={submitting || code.length !== 6} style={{
                  ...btnPrimary, opacity: (submitting || code.length !== 6) ? 0.5 : 1,
                }}>
                  {submitting ? 'Verifying…' : 'Verify & Continue'}
                </button>
              </form>
            )}

            {/* Step 2b: 2FA Verify */}
            {step === '2fa_verify' && (
              <form onSubmit={handleVerify2FA}>
                <div style={{ textAlign: 'center', marginBottom: 24, color: COLORS.authTextMuted, fontSize: 15 }}>
                  Enter the 6-digit code from your authenticator app.
                </div>
                <div style={{ marginBottom: 24 }}>
                  <input
                    ref={codeRef}
                    type="text" value={code} maxLength={6} inputMode="numeric"
                    onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); clearErrors(); }}
                    placeholder="000000" required
                    style={{ ...inputStyle, textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: '0.3em' }}
                  />
                </div>
                <button type="submit" disabled={submitting || code.length !== 6} style={{
                  ...btnPrimary, opacity: (submitting || code.length !== 6) ? 0.5 : 1,
                }}>
                  {submitting ? 'Verifying…' : 'Verify'}
                </button>
                <button type="button"
                  onClick={() => { setStep('credentials'); setCode(''); clearErrors(); }}
                  style={{
                    width: '100%', marginTop: 12, padding: 14, fontSize: 14,
                    fontWeight: 600, border: 'none', borderRadius: 10,
                    background: 'transparent', color: COLORS.authTextHelper, cursor: 'pointer',
                  }}
                >← Back to Sign In</button>
              </form>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 14, fontWeight: 600, color: COLORS.authTextLabel, marginBottom: 8,
};

const inputStyle = {
  width: '100%', padding: '14px 16px', fontSize: 15,
  border: `1px solid ${COLORS.authInputBorder}`, borderRadius: 10,
  background: COLORS.authInputBg, color: COLORS.authText,
  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};

const btnPrimary = {
  width: '100%', padding: 16, border: 'none', borderRadius: 10,
  background: COLORS.authPrimary, color: COLORS.authText, fontSize: 16, fontWeight: 700,
  cursor: 'pointer',
};
